// Recurring-task helpers shared by TaskCreate and TaskEdit.
// Lives inside the mirrored hrms tree so it syncs to Teamlio.

export type RecurrenceInterval = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface RecurrenceFormState {
  is_recurring?: boolean;
  recurrence_interval?: RecurrenceInterval;
  recurrence_every?: number;
}

export interface RecurrenceRule {
  interval: RecurrenceInterval;
  every: number;
  next_spawn_at: string; // naive ISO "YYYY-MM-DDTHH:MM:SS" — NO Z / NO offset
}

const clampEvery = (every: unknown): number =>
  Math.max(1, Math.floor(Number(every) || 1));

/**
 * Format a Date as a NAIVE ISO string "YYYY-MM-DDTHH:MM:SS".
 * NOT toISOString() — the backend compares this against a timezone-naive
 * datetime, so a tz-aware string (Z/offset) would crash the spawn job.
 */
export function toNaiveIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
}

/**
 * First spawn = `from` + one interval, mirroring the backend `_next_spawn`
 * cadence (monthly = 30-day steps, not calendar months).
 */
export function computeNextSpawnAt(
  interval: RecurrenceInterval,
  every: number,
  from: Date = new Date(),
): string {
  const n = clampEvery(every);
  const d = new Date(from.getTime());
  if (interval === 'DAILY') d.setDate(d.getDate() + n);
  else if (interval === 'WEEKLY') d.setDate(d.getDate() + n * 7);
  else if (interval === 'MONTHLY') d.setDate(d.getDate() + n * 30);
  return toNaiveIso(d);
}

/** Build the {is_recurring, recurrence_rule} payload fragment for create + update. */
export function buildRecurrence(
  form: RecurrenceFormState,
  from?: Date,
): { is_recurring: boolean; recurrence_rule: RecurrenceRule | null } {
  if (!form.is_recurring) return { is_recurring: false, recurrence_rule: null };
  const interval = form.recurrence_interval || 'WEEKLY';
  const every = clampEvery(form.recurrence_every);
  return {
    is_recurring: true,
    recurrence_rule: { interval, every, next_spawn_at: computeNextSpawnAt(interval, every, from) },
  };
}

/** Helper-text unit word, pluralized by count. */
export function intervalUnit(interval: RecurrenceInterval, every: number): string {
  const base = interval === 'DAILY' ? 'day' : interval === 'WEEKLY' ? 'week' : 'month';
  return clampEvery(every) > 1 ? `${base}s` : base;
}
